import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
} from '../../redux/features/issueReport/issueReport.selectors';
import { closeFlagWindow } from '../../redux/features/issueReport/issueReport.slice';

export interface IssueFlagWindow {
  issueId: string;
  expiresAt: number;
}

/**
 * HITL-1499: the single owner of the single flag-window timer.
 *
 * HITL-1498 shipped the whole lifecycle in the slice — `reportCreated` opens the
 * window synchronously (`created_at + FLAG_WINDOW_MS`) and `voidIssue` closes it
 * — but nothing closes it on EXPIRY, so an unattended window would stay open
 * forever and the undo toast would never leave. That timer is this hook.
 *
 * Mount it ONCE (the toast does). Two consumers each running their own timeout is
 * exactly how the undo deadline and HITL-1500's tray would drift apart under
 * load.
 *
 * The remaining time is derived from `expiresAt` rather than counted down from 5,
 * so a re-render, a throttled tab or HackTimer cannot stretch the window.
 */
const useIssueFlagWindow = (): IssueFlagWindow | null => {
  const dispatch = useAppDispatch();
  // Two flat fields on the slice, not a nested object.
  const activeReport = useSelector(selectActiveIssueReport);
  const expiresAt = useSelector(selectActiveFlagWindowExpiresAt);
  const issueId = activeReport?.issue_id;

  // One timeout, keyed on the window identity. A re-flag replaces the window, so
  // the effect re-runs and the previous timer is cleared — no drift, no orphan.
  useEffect(() => {
    if (!expiresAt) return undefined;
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      dispatch(closeFlagWindow());
      return undefined;
    }
    const timer = setTimeout(() => dispatch(closeFlagWindow()), remaining);
    return () => clearTimeout(timer);
  }, [issueId, expiresAt, dispatch]);

  return issueId && expiresAt ? { issueId, expiresAt } : null;
};

export default useIssueFlagWindow;
